import * as Tooltip from '@radix-ui/react-tooltip';
import {useArtifactWeaveReference} from '@wandb/weave/common/hooks/useArtifactWeaveReference';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import {type WandbArtifactRef} from '@wandb/weave/react';
import {clsx} from 'clsx';
import React, {type FC} from 'react';

import {IconNames} from '../../../../Icon';
import {fetchArtifactRefPageUrl} from '../artifactRegistry';
import {AnchorLink} from '../pages/common/Links';
import {SmallRefBox} from './SmallRefBox';

export const SmallArtifactRef: FC<{
  objRef: WandbArtifactRef;
  iconOnly?: boolean;
  displayName?: string;
}> = ({objRef, displayName}) => {
  const {loading, artInfo} = useArtifactWeaveReference({
    entityName: objRef.entityName,
    projectName: objRef.projectName,
    artifactName: objRef.artifactName + ':' + objRef.artifactVersion,
  });
  if (loading) {
    return (
      <TailwindContents>
        <div className="flex h-full w-full min-h-[38px] items-center">
          <SmallRefBox iconName={IconNames.Loading} text="Loading..." />
        </div>
      </TailwindContents>
    );
  }

  const artifactUrl = artInfo
    ? fetchArtifactRefPageUrl({
        entityName: objRef.entityName,
        projectName: objRef.projectName,
        artifactName: objRef.artifactName,
        artifactVersion: objRef.artifactVersion,
        artifactType: artInfo?.artifactType,
        orgName: artInfo?.orgName,
      })
    : null;

  const Content = (
    <Tooltip.Provider delayDuration={150} skipDelayDuration={50}>
      <Tooltip.Root>
        <TailwindContents>
          <div
            className={clsx(
              'flex h-full w-full min-h-[38px] items-center',
              artifactUrl ? 'cursor-pointer' : 'cursor-not-allowed'
            )}>
            <Tooltip.Trigger asChild>
              <div className="flex items-center">
                <SmallRefBox
                  iconName={IconNames.Registries}
                  text={
                    displayName ??
                    `${objRef.artifactName}:${objRef.artifactVersion}`
                  }
                />
              </div>
            </Tooltip.Trigger>
          </div>
        </TailwindContents>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={8}
            className="rounded bg-moon-900 px-3 py-2 text-sm text-moon-200"
            style={{
              zIndex: 9999,
              position: 'relative',
              backgroundColor: '#1a1a1a',
              color: '#e0e0e0',
              padding: '8px 12px',
              borderRadius: '4px',
            }}>
            {artifactUrl
              ? objRef.artifactPath
              : 'No link detected for this wandb artifact reference: ' +
                objRef.artifactPath}
            <Tooltip.Arrow style={{fill: '#1a1a1a'}} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );

  return artifactUrl ? (
    <AnchorLink
      $variant="secondary"
      style={{width: '100%', height: '100%'}}
      href={artifactUrl}>
      {Content}
    </AnchorLink>
  ) : (
    Content
  );
};
